"use client";

import { Separator } from "@/components/ui/separator";
import type { Issue } from "../constants";
import {
	organizeIssuesBySection,
	sortIssuesByPriority,
	validateIssuesByState,
} from "../helpers";
import { RoadmapCard } from "./RoadmapCard";
import { RoadmapEmptyState } from "./RoadmapEmptyState";

type SectionNames = {
	planned: string;
	inProgress: string;
	completed: string;
};

type StateMappings = {
	planned: string[];
	inProgress: string[];
	completed: string[];
};

type RoadmapGridProps = {
	issues: Issue[];
	sectionNames: SectionNames;
	stateMappings: StateMappings;
};

export const RoadmapGrid = ({
	issues,
	sectionNames,
	stateMappings,
}: RoadmapGridProps) => {
	const validIssues = validateIssuesByState(issues, stateMappings);
	const organizedIssues = organizeIssuesBySection(validIssues, stateMappings);
	const sections = Object.entries(organizedIssues) as [
		keyof typeof organizedIssues,
		Issue[],
	][];

	if (validIssues.length === 0) {
		return <RoadmapEmptyState />;
	}

	return (
		<div className="space-y-4">
			<Separator className="opacity-90" />
			<div className="grid grid-cols-1 gap-16 xl:h-[calc(100vh-12rem)] xl:grid-cols-3 xl:gap-0">
				{sections.map(([section, sectionIssues], index) => {
					const sectionName = sectionNames[section];
					return (
						<div key={section} className="relative flex flex-col px-4">
							<h2 className="mb-4 font-semibold text-2xl">{sectionName}</h2>
							<div className="xl:scrollbar-thin xl:scrollbar-track-transparent xl:scrollbar-thumb-muted w-full overflow-hidden xl:h-[calc(100vh-16rem)] xl:overflow-y-auto xl:pr-4">
								{sectionIssues.length === 0 ? (
									<RoadmapEmptyState
										message={`No ${sectionName?.toLowerCase()} items`}
									/>
								) : (
									<div className="grid gap-4">
										{sectionIssues
											.sort(sortIssuesByPriority)
											.map((issue: Issue) => (
												<RoadmapCard key={issue.id} issue={issue} />
											))}
									</div>
								)}
							</div>
							{index < sections.length - 1 && (
								<Separator
									orientation="vertical"
									className="absolute -top-4 right-0 hidden h-full opacity-90 xl:block"
								/>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};
